<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const contacts = ref([]);
const isLoading = ref(false);

const byStatus = status =>
  contacts.value.filter(c => c.status === status).length;

const sentCount = computed(() => byStatus('sent'));
const failedCount = computed(() => byStatus('failed'));
const skippedCount = computed(() => byStatus('skipped'));
const pendingCount = computed(
  () =>
    contacts.value.filter(c => ['pending', 'scheduled'].includes(c.status))
      .length
);
const totalCount = computed(() => contacts.value.length);

const summaryBadges = computed(() => [
  {
    key: 'sent',
    label: t('CAMPAIGN.WAHA.CONTACTS_DIALOG.SENT', { count: sentCount.value }),
    class: 'text-n-teal-11 bg-n-teal-2',
    show: true,
  },
  {
    key: 'failed',
    label: t('CAMPAIGN.WAHA.CONTACTS_DIALOG.FAILED', {
      count: failedCount.value,
    }),
    class: 'text-n-ruby-11 bg-n-ruby-2',
    show: true,
  },
  {
    key: 'pending',
    label: t('CAMPAIGN.WAHA.CONTACTS_DIALOG.PENDING', {
      count: pendingCount.value,
    }),
    class: 'text-n-blue-11 bg-n-blue-2',
    show: true,
  },
  {
    key: 'skipped',
    label: t('CAMPAIGN.WAHA.CONTACTS_DIALOG.SKIPPED', {
      count: skippedCount.value,
    }),
    class: 'text-n-amber-11 bg-n-amber-2',
    show: skippedCount.value > 0,
  },
]);

const statusBadgeClass = status => {
  const map = {
    sent: 'text-n-teal-11 bg-n-teal-2',
    failed: 'text-n-ruby-11 bg-n-ruby-2',
    pending: 'text-n-slate-11 bg-n-surface-2',
    scheduled: 'text-n-blue-11 bg-n-blue-2',
    skipped: 'text-n-amber-11 bg-n-amber-2',
  };
  return map[status] || 'text-n-slate-11 bg-n-surface-2';
};

const statusLabel = status =>
  t(`CAMPAIGN.WAHA.CONTACTS_DIALOG.STATUS.${status.toUpperCase()}`);

const loadContacts = async () => {
  if (!props.campaign) return;
  isLoading.value = true;
  await store.dispatch('campaigns/fetchContacts', props.campaign.id);
  contacts.value =
    store.getters['campaigns/getCampaignContacts'](props.campaign.id) || [];
  isLoading.value = false;
};

watch(
  () => props.campaign,
  val => {
    if (val) loadContacts();
  }
);

const open = async () => {
  await loadContacts();
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="t('CAMPAIGN.WAHA.CONTACTS_DIALOG.TITLE')"
    :show-confirm-button="false"
    :cancel-button-label="t('CAMPAIGN.WAHA.CONTACTS_DIALOG.CLOSE')"
    overflow-y-auto
  >
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-3 flex-wrap">
        <span
          v-for="badge in summaryBadges"
          v-show="badge.show"
          :key="badge.key"
          class="text-xs font-medium px-2 py-0.5 rounded-full"
          :class="badge.class"
        >
          {{ badge.label }}
        </span>
        <span class="text-xs text-n-slate-10 ml-auto">
          {{ t('CAMPAIGN.WAHA.CONTACTS_DIALOG.TOTAL', { count: totalCount }) }}
        </span>
      </div>

      <div
        v-if="isLoading"
        class="flex items-center justify-center py-8 text-n-slate-11"
      >
        <span class="text-sm">
          {{ t('CAMPAIGN.WAHA.CONTACTS_DIALOG.LOADING') }}
        </span>
      </div>

      <div
        v-else-if="contacts.length === 0"
        class="py-8 text-center text-sm text-n-slate-10"
      >
        {{ t('CAMPAIGN.WAHA.CONTACTS_DIALOG.EMPTY') }}
      </div>

      <div v-else class="flex flex-col divide-y divide-n-weak">
        <div
          v-for="contact in contacts"
          :key="contact.id"
          class="flex items-center justify-between py-2.5 gap-3"
        >
          <span class="text-sm text-n-slate-12 truncate flex-1">
            {{ contact.name || contact.contact_id }}
          </span>
          <span
            class="text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0"
            :class="statusBadgeClass(contact.status)"
          >
            {{ statusLabel(contact.status) }}
          </span>
        </div>
      </div>
    </div>
  </Dialog>
</template>
